import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AsyncLoaderControl } from "@/Common/Components/Loader/AsyncLoaderControl";
import { Popup } from "@/Common/Components/Modals/Popup";
import { Toast } from "@/Common/Components/Toast/Toast";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { Locale } from "@/Common/Models/Localization/Locale";
import { ILoginModel } from "@/Common/Models/UI/Login/ILoginModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { LoginWorkflow } from "@/Common/Workflow/LoginWorkflow/LoginWorkflow";
import { LoginWorkflowStepEnum } from "@/Common/Workflow/LoginWorkflow/LoginWorkflowStepEnum";
import { Button, LinkButton } from "@/DesignSystem";
import { RequestResetPasswordControl } from "../PasswordResetApp/RequestResetPasswordControl";
import { LoginWithExternalIdentityProviderView } from "./Login/LoginWithExternalIdentityProviderView";
import { LoginWorkflowPopups } from "./Login/LoginWorkflowPopups";
import { NewExperienceLoginAuthenticatorView } from "./Login/NewExperienceLoginAuthenticatorView";
import { NewExperienceLoginCredentialsView } from "./Login/NewExperienceLoginCredentialsView";
import { UrlService } from "./Services/UrlService";

import styles from "./NewExperienceLoginForm.module.scss";

import ArrowLeftIcon from "@/DesignSystem/Atoms/Icons/Types/ArrowLeft/arrow-left-regular.svg";

interface INewExperienceLoginFormProps {
    formTitle: string;
    loginButtonTextKey: string;
    loginOperation: LocalOperations.Login;
    language: Locale;
    stringProvider: ResourceProvider;
    isUserRelogin?: boolean;
}

export function NewExperienceLoginForm(props: Readonly<INewExperienceLoginFormProps>): JSX.Element {
    const navigate = useNavigate();
    const loginWithExternalIdentity = cacheStore.ClinicSettings.EnableSSOAuthentication;
    const [model, setModel] = useState<ILoginModel>(sessionStore.clientLoginModel);
    const [loginWorkflow, setLoginWorkflow] = useState<LoginWorkflow>(sessionStore.loginWorkflow);
    const [loginWorkflowStep, setLoginWorkflowStep] = useState<LoginWorkflowStepEnum>(sessionStore.loginWorkflow?.getCurrentStep()?.stepEnum);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [trackApplicationStatusUrl, setTrackApplicationStatusUrl] = useState<string>();
    const [remoteAccessUrl, setRemoteAccessUrl] = useState<string>();
    const [shouldLoginWithExternalIdentityProvider, setShouldLoginWithExternalIdentityProvider] = useState<boolean>(loginWithExternalIdentity);
    const [password, setPassword] = useState<string>("");
    const [hasExternalError, setHasExternalError] = useState(false);
    const onUsernameChange = (username: string): void => {
        sessionStore.dispatch(LocalOperations.Login.Username_TextChange, { UserName: username });
    };

    const onPasswordChange = (password: string): void => {
        sessionStore.dispatch(LocalOperations.Login.Password_TextChange, { Password: password });
        setPassword(password);
    };

    const toggleLoginWithExternalIdentityProvider = (): void => {
        setShouldLoginWithExternalIdentityProvider(!shouldLoginWithExternalIdentityProvider);
    };

    const renderPhoneNumbers = (): JSX.Element => {
        const provinces = sessionStore.ClinicActiveProvinces || [];
        const differentCountries = Array.from(new Set(provinces.map(province => province.CountryLookupCode)));
        const differentStates = Array.from(new Set(provinces.map(province => province.StateLookupCode)));

        if (differentCountries.length > 1 || differentStates.length > 1) {
            return (<></>);
        }

        // The lookupIds are hardcoded since there is no lookupProvider at this stage
        switch (differentStates?.[0]) {
            case CanadaState.Ontario:
                return (
                    <div>{props.stringProvider.GetString("OntarioPhoneLabel")}</div>
                );
            case CanadaState.Quebec:
                return (
                    <div>{props.stringProvider.GetString("QuebecPhoneLabel")}</div>
                );
            case CanadaState.BritishColumbia:
                return (
                    <div>{props.stringProvider.GetString("BritishColumbiaPhoneLabel")}</div>
                );
            default:
                return (<></>);
        }
    };

    const renderErrorMessages = (): JSX.Element | null => {
        const errorMessage = model.get("ErrorMessage");

        if (!errorMessage) return null;

        const message = errorMessage.UseLocalResource
            ? props.stringProvider.GetString(errorMessage.Message)
            : errorMessage.Message;

        const activationDate = errorMessage.AccountActiveDate
            ? LuxonDateTimeParser.parse(errorMessage.AccountActiveDate).toLocal().toFormat("yyyy/MM/dd")
            : null;

        return (
            <div data-cy="NewExperienceLogin-error-message" className="error-message">
                <strong>
                    {message}
                    {activationDate && (
                        <>
                            {" "}
                            {errorMessage.UseLocalResource ? "" : "Activation Date: "}
                            {activationDate}
                        </>
                    )}
                </strong>
            </div>
        );
    };

    useEffect(() => {
        if (typeof globalThis !== "undefined") {
            const params = new URLSearchParams(globalThis.location.search);
            const errorCode = params.get("error");
            const errorDescription = params.get("error_description");

            if (!IsNullOrWhiteSpace(errorCode) || !IsNullOrWhiteSpace(errorDescription)) {
                setHasExternalError(true);
                navigate("/", { replace: true });
            }
        }
    }, [navigate]);

    const getLoginFormContent = (): { loginTitle: string; loginView: JSX.Element } => {
        const displayAuthenticatorCodeForm = model.get("ShowAuthenticatorCodeForm");

        if (displayAuthenticatorCodeForm) {
            return {
                loginTitle: props.stringProvider.GetString("NewExperienceLogin_AuthenticatorVerificationCodeLabel"),
                loginView: (
                    <NewExperienceLoginAuthenticatorView
                        loginPageModel={model}
                        stringProvider={props.stringProvider}
                        errorMessageJsx={renderErrorMessages()}
                        loginOperation={props.loginOperation}
                    />
                )
            };
        }

        return {
            loginTitle: props.formTitle,
            loginView: shouldLoginWithExternalIdentityProvider
                ? (
                    <LoginWithExternalIdentityProviderView
                        stringProvider={props.stringProvider}
                        onSubmit={provider => document.location.href = `${rootPath}/auth/challenge/${provider}`}
                        hasExternalError={hasExternalError}
                    />
                )
                : (
                    <NewExperienceLoginCredentialsView
                        userName={model.get("UserName")}
                        validatingCredentials={model.get("ValidatingCredentials")}
                        isLoggedIn={model.get("LoggedIn")}
                        stringProvider={props.stringProvider}
                        errorMessageJsx={renderErrorMessages()}
                        buttonTextLabelKey={props.loginButtonTextKey}
                        isUserRelogin={props.isUserRelogin}
                        isLoading={isLoading}
                        onUsernameChange={onUsernameChange}
                        onPasswordChange={onPasswordChange}
                        onSubmit={() =>
                            sessionStore.dispatch(props.loginOperation)
                        }
                    />
                )
        };
    };

    useEffect(() => {
        const storeChangeListener = (): void => {
            setModel(sessionStore.clientLoginModel);
            setLoginWorkflow(sessionStore.loginWorkflow);
            setIsLoading(sessionStore.loading);
            const newStep = sessionStore.loginWorkflow?.getCurrentStep();
            setLoginWorkflowStep(newStep?.stepEnum);
        };

        const sessionStoreChangeListenerId = sessionStore.AddChangeListener(storeChangeListener, null);
        return () => sessionStore.RemoveChangeListener(sessionStoreChangeListenerId);
    }, []);

    useEffect(() => {
        setTrackApplicationStatusUrl(UrlService.getTrackApplicationStatusUrl(props.language));
        setRemoteAccessUrl(UrlService.getRemoteAccessUrl());
    }, [props.language]);

    const renderPopups = (): JSX.Element => (
        <>
            {loginWorkflow && (
                <LoginWorkflowPopups
                    key={loginWorkflowStep?.toString() ?? ""}
                    stringProvider={props.stringProvider}
                    loginWorkflow={loginWorkflow}
                    loginModel={model}
                    password={password}
                    onPasswordChange={newPassword => setPassword(newPassword)}
                />
            )}
            {model.get("OpenRequestResetPasswordControl") && (
                <RequestResetPasswordControl
                    popupTitle="PasswordRecoveryTitle"
                    stringProvider={props.stringProvider}
                    currentlyProcessing={model.get("RequestResetPasswordInProgress")}
                    success={model.get("RequestResetPasswordSuccessful")}
                    errors={model.get("RequestResetPasswordErrorMessage")?.Message}
                    onClosePopupCallBack={() => sessionStore.dispatch(LocalOperations.Login.CloseRequestResetPasswordControl)}
                    onResetPassword={emailAddress =>
                        sessionStore.dispatch(LocalOperations.Login.RequestResetPassword, {
                            emailAddress: emailAddress,
                            language: props.language
                        })
                    }
                />
            )}
            {model.get("ShowPrivacyPolicy") && sessionStore.acceptedAgreement !== false && (
                <Popup
                    cssClass={styles.privacyPopup}
                    title={props.stringProvider.GetString("DisclaimerHeaderLabel")}
                    closeTarget={LocalOperations.Login.ClosePrivacyPolicy}
                >
                    <div>
                        <div className="p-body">
                            <div>
                                <div>
                                    {props.stringProvider.GetString("DisclaimerContent")}
                                </div>
                            </div>
                        </div>
                        <div className="p-footer">
                            <div className={styles.privacyFooter}>
                                <Button
                                    variant="brand"
                                    label={props.stringProvider.GetString("OKLabel")}
                                    onClick={() => sessionStore.dispatch(LocalOperations.Login.ClosePrivacyPolicy)}
                                />
                            </div>
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );

    if (!model || !props.stringProvider)
        return null;

    const { loginTitle, loginView } = getLoginFormContent();

    return (
        <div className={styles.loginFormContainer}>
            <div className={styles.loginForm}>
                {loginWithExternalIdentity && !shouldLoginWithExternalIdentityProvider && (
                    <div className={styles.backButtonWrapper}>
                        <LinkButton
                            variant="neutral"
                            underline={false}
                            label={props.stringProvider.GetString("BackLabel")}
                            onClick={toggleLoginWithExternalIdentityProvider}
                        >
                            <ArrowLeftIcon />
                        </LinkButton>
                    </div>
                )}
                <div className={styles.loginTitle}>
                    {loginTitle}
                </div>
                {loginView}
                {!shouldLoginWithExternalIdentityProvider && (
                    <div className={styles.forgotPasswordLink}>
                        <LinkButton
                            variant="neutral"
                            label={props.stringProvider.GetString("NewExperienceLogin_ForgotPasswordLabel")}
                            onClick={() => sessionStore.dispatch(LocalOperations.Login.OpenRequestResetPasswordControl)}
                        />
                    </div>
                )}
                {props.isUserRelogin && (
                    <div className={styles.redirectToLoginLink}>
                        <LinkButton
                            variant="neutral"
                            label={props.stringProvider.GetString("SignInOtherAccountLabel")}
                            onClick={() => MyleHelper.redirectToLogin()}
                        />
                    </div>
                )}
                <div className={styles.firstLevelLinks}>
                    <LinkButton
                        variant="neutral"
                        label={props.stringProvider.GetString("privacyPolicy")}
                        onClick={() => sessionStore.dispatch(LocalOperations.Login.ShowPrivacyPolicy)}
                    />
                    <span className={styles.circleSeparator} />
                    <LinkButton
                        variant="neutral"
                        label={props.stringProvider.GetString("trackApplicationStatus")}
                        onClick={() => window.open(trackApplicationStatusUrl)}
                    />
                </div>
                <div className={styles.secondLevelLinks}>
                    <LinkButton
                        variant="neutral"
                        label={props.stringProvider.GetString("remoteControl")}
                        onClick={() => window.open(remoteAccessUrl)}
                    />
                    {shouldLoginWithExternalIdentityProvider && (
                        <>
                            <span className={styles.circleSeparator} />
                            <LinkButton
                                variant="neutral"
                                label={props.stringProvider.GetString("NewExperienceLogin_LoginWithUserNameLabel")}
                                onClick={toggleLoginWithExternalIdentityProvider}
                            />
                        </>
                    )}
                </div>
                <div className={styles.phoneNumberSection}>
                    {renderPhoneNumbers()}
                    <div>{props.stringProvider.GetString("TollFreePhoneLabel")}</div>
                </div>
                <div className={styles.mailToSupportLink}>
                    <LinkButton
                        label="support@medfarsolutions.com"
                        underline={false}
                        onClick={() => document.location.href = "mailto:support@medfarsolutions.com"}
                    />
                </div>
                <div className={styles.buttonContainer}>
                    <Button
                        onClick={() => window.open(UrlService.getHelpUrl(props.language))}
                        label={props.stringProvider.GetString("MyleHelpLabel")}
                        disabled={model.get("ValidatingCredentials")}
                        variant="neutral"
                        category="secondary"
                        size="lg"
                    />
                </div>
                <div>
                    <div id="Version" className={styles.versionContainer}>
                        {sessionStore.versionNumber}
                    </div>
                </div>
            </div>
            {renderPopups()}
            <AsyncLoaderControl IsActive={isLoading} />
            <Toast />
        </div>
    );
}
